import React from 'react';
import { SearchIndexItem } from '@/core/domain/types/search.types';
import { CheckCircle2, Star, ChevronRight, Calendar, ArrowRight, MapPin, Building, Video, Home } from 'lucide-react';

interface DoctorCardProps {
  item: SearchIndexItem;
  onPress?: () => void;
}

export function DoctorCard({ item, onPress }: Readonly<DoctorCardProps>) {
  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    const words = name.replace(/^(Dr\.|Dra\.|Lic\.|Psic\.)\s*/i, '').trim().split(' ');
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const initials = getInitials(item.nombre);
  
  // Extraemos las modalidades buscando en el blob de texto (case-insensitive)
  const searchBlob = item.search_blob?.toLowerCase() || '';
  let isPresencial = searchBlob.includes('presencial') || searchBlob.includes('consultorio');
  let isVirtual = searchBlob.includes('virtual') || searchBlob.includes('online') || searchBlob.includes('telemedicina');
  let isDomicilio = searchBlob.includes('domicilio') || searchBlob.includes('casa') || searchBlob.includes('hogar');

  // Fallback de seguridad: Si no tiene registrada ninguna modalidad, forzamos Consultorio (presencial)
  if (!isPresencial && !isVirtual && !isDomicilio) {
    isPresencial = true;
  }

  // Banners dinámicos de urgencia movidos a fase Post-MVP

  return (
    <div style={{
      backgroundColor: '#FFFFFF',
      borderRadius: '24px',
      padding: '20px',
      marginBottom: '16px',
      boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
      border: '1px solid #F8FAFC',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      width: '100%',
      textAlign: 'left',
      display: 'block'
    }}
    onClick={onPress}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0px 12px 32px rgba(15, 23, 42, 0.08)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0px 8px 24px rgba(15, 23, 42, 0.04)';
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        {item.imagen_url ? (
          <img src={item.imagen_url} alt={item.nombre} style={{ width: '64px', height: '64px', borderRadius: '32px', marginRight: '16px', backgroundColor: '#F1F5F9', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '64px', height: '64px', borderRadius: '32px', backgroundColor: '#E0E7FF', display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px', border: '2px solid #EEF2FF', flexShrink: 0 }}>
            <span style={{ color: '#4338CA', fontSize: '22px', fontWeight: 800, letterSpacing: '0.5px' }}>{initials}</span>
          </div>
        )}

        <div style={{ flex: 1, justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0F172A', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.nombre}
            </h3>
            {item.verificado && (
              <CheckCircle2 size={18} color="#3B82F6" style={{ marginLeft: '6px', flexShrink: 0 }} />
            )}
          </div>
          
          <p style={{ fontSize: '14px', color: '#475569', fontWeight: 600, margin: '0 0 4px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.titulo_principal || 'Profesional Clínico'}{item.especialidad ? ` • ${item.especialidad}` : ''}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            {Array.from({ length: 5 }, (_, i) => i).map((starId) => (
              <Star key={`star-${starId}`} size={12} fill="#F59E0B" color="#F59E0B" style={{ marginRight: '2px' }} />
            ))}
            <span style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, marginLeft: '4px' }}>(Pronto)</span>
          </div>

          {item.licencia_profesional && (
            <p style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 500, margin: '4px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Licencia: {item.licencia_profesional}
            </p>
          )}
        </div>

        <div style={{ paddingLeft: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ChevronRight size={24} color="#CBD5E1" />
        </div>
      </div>

      <div style={{ paddingTop: '16px', borderTop: '1px solid #F1F5F9' }}>
        {item.agenda_activa ? (
          <div style={{ marginBottom: '12px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); if (onPress) onPress(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 16px',
                borderRadius: '12px', gap: '6px', backgroundColor: '#0F172A', border: 'none', cursor: 'pointer',
                color: '#FFFFFF', fontWeight: 700, fontSize: '14px', transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Agendar Cita
              <ArrowRight size={16} color="#FFFFFF" />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#F8FAFC', padding: '10px', borderRadius: '8px', marginBottom: '12px' }}>
            <Calendar size={16} color="#94A3B8" />
            <span style={{ fontSize: '13px', color: '#64748B', fontWeight: 600, marginLeft: '6px' }}>Agenda no configurada para reservas</span>
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {item.distance_km !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', borderRadius: '100px', gap: '6px', backgroundColor: '#FEF2F2' }}>
              <MapPin size={14} color="#EF4444" />
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#EF4444' }}>
                A {item.distance_km < 1 ? '< 1' : item.distance_km.toFixed(1)} km
              </span>
            </div>
          )}
          {isPresencial && (
            <div style={{ width: '32px', height: '32px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#ECFDF5' }} title="Presencial / Consultorio">
              <Building size={16} color="#059669" />
            </div>
          )}
          {isVirtual && (
            <div style={{ width: '32px', height: '32px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#EFF6FF' }} title="Virtual / Telemedicina">
              <Video size={16} color="#2563EB" />
            </div>
          )}
          {isDomicilio && (
            <div style={{ width: '32px', height: '32px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#FAF5FF' }} title="A Domicilio">
              <Home size={16} color="#9333EA" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
